import React, { useEffect, useState } from 'react';
import axios from 'axios';
import API_BASE from '../config';

const Admin = () => {
  const backend = API_BASE;
  const token = localStorage.getItem('token');

  const [books, setBooks] = useState([]);
  const [editing, setEditing] = useState(null);
  const [message, setMessage] = useState('');

  // form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [cover, setCover] = useState(null);
  const [cbz, setCbz] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);


  // =========================
  // FETCH BOOKS
  // =========================
  const fetchBooks = async () => {
  try {
    const res = await axios.get(`${backend}/books`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    setBooks(res.data);
  } catch (err) {
    setMessage(err.response?.data?.message || 'Failed to load books');
  }
};

  useEffect(() => {
    fetchBooks();
  }, []);

  // =========================
  // START EDIT
  // =========================
  const startEdit = (book) => {
    setEditing(book.id);
    setTitle(book.title);
    setDescription(book.description || '');
    setCover(null);
    setCbz(null);
  };

  // =========================
  // CANCEL EDIT
  // =========================
  const cancelEdit = () => {
    setEditing(null);
    setTitle('');
    setDescription('');
    setCover(null);
    setCbz(null);
  };

  // =========================
  // SAVE EDIT
  // =========================
  const saveEdit = async (bookId) => {
    try {
      const formData = new FormData();
      formData.append('title', title);
      formData.append('description', description);

      if (cover) formData.append('cover', cover);
      if (cbz) formData.append('cbz', cbz);

      await axios.put(`${backend}/admin/book/${bookId}`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setMessage('Book updated!');
      cancelEdit();
      fetchBooks();
    } catch (err) {
      setMessage(err.response?.data?.message || 'Update failed');
    }
  };

  // =========================
  // DELETE BOOK
  // =========================
  const deleteBook = async (bookId) => {
    if (!window.confirm('Delete this book?')) return;

    try {
      await axios.delete(`${backend}/admin/book/${bookId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setMessage('Book deleted');
      fetchBooks();
    } catch {
      setMessage('Delete failed');
    }
  };

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-6xl mx-auto">

        <h1 className="text-4xl font-display mb-6">Admin Panel</h1>

        <p className="text-red-500 mb-4">{message}</p>

        <button onClick={() => setShowUpload(!showUpload)} className="w-full bg-purple-600 text-white py-3 rounded mb-6">
          {showUpload ? 'Close Upload' : 'Upload New Book'}
        </button>

        {showUpload && (
          <div className="mb-8 p-6 border rounded bg-white shadow">

            <h2 className="text-xl font-bold mb-4">Upload New Book</h2>

            {/* TITLE */}
            <input
              disabled={uploading}
              className="w-full border p-2 mb-2"
              placeholder="Title"
              value={title}
              onChange={e => setTitle(e.target.value)}
            />

            {/* DESCRIPTION */}
            <textarea
              disabled={uploading}
              className="w-full border p-2 mb-4"
              placeholder="Description"
              value={description}
              onChange={e => setDescription(e.target.value)}
            />

            {/* DRAG & DROP AREA */}
            <div
              onDragOver={(e) => {
                e.preventDefault();
                setDragActive(true);
              }}
              onDragLeave={() => setDragActive(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragActive(false);

                const files = Array.from(e.dataTransfer.files);

                files.forEach(file => {
                  if (file.name.endsWith('.cbz')) setCbz(file);
                  else if (file.type.startsWith('image/')) setCover(file);
                });
              }}
              className={`border-2 border-dashed p-6 text-center mb-4 cursor-pointer transition
                ${dragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300'}`}
            >
              <p className="text-sm">
                Drag & drop CBZ + cover here, or use file inputs below
              </p>
            </div>

            {/* FILE INPUTS */}
            <div className="mb-2">
              <label>Cover Image:</label>
              <input
                type="file"
                disabled={uploading}
                onChange={e => setCover(e.target.files[0])}
              />
            </div>

            <div className="mb-4">
              <label>CBZ File:</label>
              <input
                type="file"
                disabled={uploading}
                onChange={e => setCbz(e.target.files[0])}
              />
            </div>

            {/* PROGRESS BAR */}
            {uploading && (
              <div className="mb-4">
                <div className="w-full bg-gray-200 rounded h-4">
                  <div
                    className="bg-green-500 h-4 rounded transition-all"
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <p className="text-sm mt-1">{progress}%</p>
              </div>
            )}

            {/* UPLOAD BUTTON */}
            <button
              disabled={uploading}
              onClick={async () => {
                if (!title || !cover || !cbz) {
                  return setMessage('Missing required fields');
                }

                try {
                  setUploading(true);
                  setProgress(0);
                  setMessage('Starting upload...');

                  const formData = new FormData();
                  formData.append('title', title);
                  formData.append('description', description);
                  formData.append('cover', cover);
                  formData.append('cbz', cbz);

                  const res = await axios.post(`${backend}/admin/upload`, formData, {
                    headers: {
                      Authorization: `Bearer ${token}`,
                      'Content-Type': 'multipart/form-data'
                    },
                    timeout: 0, // 🚨 CRITICAL for large uploads
                    onUploadProgress: (event) => {
                      if (event.total) {
                        const percent = Math.round((event.loaded * 100) / event.total);
                        setProgress(percent);
                      }
                    }
                  });

                  console.log('UPLOAD SUCCESS:', res.data);

                  setMessage(res.data.message || 'Book uploaded!');
                  setTitle('');
                  setDescription('');
                  setCover(null);
                  setCbz(null);
                  setProgress(0);
                  setShowUpload(false);
                  fetchBooks();

                } catch (err) {
                  console.error('UPLOAD FULL ERROR:', err);

                  // 🔥 THIS IS THE IMPORTANT PART
                  if (err.response) {
                    // Server responded with error
                    setMessage(
                      `Server Error (${err.response.status}): ${
                        err.response.data?.message || JSON.stringify(err.response.data)
                      }`
                    );
                  } else if (err.request) {
                    // Request sent but no response
                    setMessage('No response from server (likely Apache limit or timeout)');
                  } else {
                    // Something else
                    setMessage(`Error: ${err.message}`);
                  }

                } finally {
                  setUploading(false);
                }
              }}
              className={`w-full py-3 rounded text-white ${
                uploading ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-600'
              }`}
            >
              {uploading ? 'Uploading...' : 'Upload Book'}
            </button>

          </div>
        )}

        <div className="grid md:grid-cols-2 gap-6">
          {books.map(book => (
            <div key={book.id} className="border p-4 rounded-lg bg-white shadow">

              {editing === book.id ? (
                <>
                  {/* EDIT FORM */}
                  <input
                    className="w-full border p-2 mb-2"
                    value={title}
                    onChange={e => setTitle(e.target.value)}
                  />

                  <textarea
                    className="w-full border p-2 mb-2"
                    value={description}
                    onChange={e => setDescription(e.target.value)}
                  />

                  <div className="mb-2">
                    <label className="text-sm">Replace Cover:</label>
                    <input type="file" onChange={e => setCover(e.target.files[0])} />
                  </div>

                  <div className="mb-2">
                    <label className="text-sm">Replace CBZ:</label>
                    <input type="file" onChange={e => setCbz(e.target.files[0])} />
                  </div>

                  <div className="flex gap-2 mt-2">
                    <button
                      onClick={() => saveEdit(book.id)}
                      className="bg-green-600 text-white px-4 py-2 rounded"
                    >
                      Save
                    </button>

                    <button
                      onClick={cancelEdit}
                      className="border px-4 py-2 rounded"
                    >
                      Cancel
                    </button>
                  </div>
                </>
              ) : (
                <>
                  {/* VIEW MODE */}
                  <h3 className="text-xl font-bold">{book.title}</h3>
                  <p className="text-sm text-muted-foreground">
                    {book.description}
                  </p>

                  <p className="text-xs mt-2">
                    Pages: {book.pageCount || 'N/A'}
                  </p>

                  <div className="flex gap-2 mt-4">
                    <button
                      onClick={() => startEdit(book)}
                      className="bg-blue-600 text-white px-3 py-1 rounded"
                    >
                      Edit
                    </button>

                    <button
                      onClick={() => deleteBook(book.id)}
                      className="bg-red-600 text-white px-3 py-1 rounded"
                    >
                      Delete
                    </button>
                  </div>
                </>
              )}

            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Admin;